/**
 * Offline Storage Manager for Shift Toolkit
 * Handles IndexedDB storage, sync queue, and offline capabilities
 */

import syncConflictResolver from './syncConflictResolver';

const DB_NAME = 'IntegrityHomeCarOffline';
const DB_VERSION = 2; // Bumped version for conflict tracking

// Store names
const STORES = {
  SHIFT_DATA: 'shiftData',
  SYNC_QUEUE: 'syncQueue',
  VITALS_QUEUE: 'vitalsQueue',
  ADL_QUEUE: 'adlQueue',
  NOTES_QUEUE: 'notesQueue',
  CONFLICT_HISTORY: 'conflictHistory'
};

class OfflineStorageManager {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.listeners = [];
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store for current shift data
        if (!db.objectStoreNames.contains(STORES.SHIFT_DATA)) {
          db.createObjectStore(STORES.SHIFT_DATA, { keyPath: 'id' });
        }

        // Store for sync queue (clock-in/out actions)
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
        }

        // Store for vitals queue
        if (!db.objectStoreNames.contains(STORES.VITALS_QUEUE)) {
          const vitalsStore = db.createObjectStore(STORES.VITALS_QUEUE, { keyPath: 'id', autoIncrement: true });
          vitalsStore.createIndex('clientId', 'clientId', { unique: false });
        }

        // Store for ADL queue
        if (!db.objectStoreNames.contains(STORES.ADL_QUEUE)) {
          const adlStore = db.createObjectStore(STORES.ADL_QUEUE, { keyPath: 'id', autoIncrement: true });
          adlStore.createIndex('shiftId', 'shiftId', { unique: false });
        }

        // Store for progress notes queue
        if (!db.objectStoreNames.contains(STORES.NOTES_QUEUE)) {
          const notesStore = db.createObjectStore(STORES.NOTES_QUEUE, { keyPath: 'id', autoIncrement: true });
          notesStore.createIndex('clientId', 'clientId', { unique: false });
        }

        // Store for conflict history
        if (!db.objectStoreNames.contains(STORES.CONFLICT_HISTORY)) {
          const conflictStore = db.createObjectStore(STORES.CONFLICT_HISTORY, { keyPath: 'id', autoIncrement: true });
          conflictStore.createIndex('resolvedAt', 'resolvedAt', { unique: false });
          conflictStore.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  // Add listener for online/offline status changes
  addStatusListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notifyListeners(status) {
    this.listeners.forEach(callback => callback(status));
  }

  handleOnline() {
    this.isOnline = true;
    this.notifyListeners({ isOnline: true });
    // Auto-sync when coming back online
    this.syncAllQueues();
  }

  handleOffline() {
    this.isOnline = false;
    this.notifyListeners({ isOnline: false });
  }

  getOnlineStatus() {
    return navigator.onLine;
  }

  // Generic method to add item to a store
  async addToStore(storeName, data) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.add({
        ...data,
        timestamp: new Date().toISOString(),
        synced: false
      });

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get all items from a store
  async getAllFromStore(storeName) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete item from store
  async deleteFromStore(storeName, id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Clear all items from a store
  async clearStore(storeName) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Save current shift data for offline access
  async saveShiftData(shiftData) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SHIFT_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.SHIFT_DATA);
      const request = store.put({ id: 'current', ...shiftData, savedAt: new Date().toISOString() });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Get saved shift data
  async getShiftData() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SHIFT_DATA], 'readonly');
      const store = transaction.objectStore(STORES.SHIFT_DATA);
      const request = store.get('current');

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Queue clock-in action
  async queueClockIn(data) {
    return this.addToStore(STORES.SYNC_QUEUE, {
      type: 'CLOCK_IN',
      data
    });
  }

  // Queue clock-out action
  async queueClockOut(data) {
    return this.addToStore(STORES.SYNC_QUEUE, {
      type: 'CLOCK_OUT',
      data
    });
  }

  // Queue ADL completion
  async queueADLCompletion(data) {
    return this.addToStore(STORES.ADL_QUEUE, data);
  }

  // Queue vitals entry
  async queueVitals(data) {
    return this.addToStore(STORES.VITALS_QUEUE, data);
  }

  // Queue progress note
  async queueProgressNote(data) {
    return this.addToStore(STORES.NOTES_QUEUE, data);
  }

  // Get pending items count
  async getPendingSyncCount() {
    if (!this.db) await this.init();
    
    const [syncQueue, vitals, adl, notes] = await Promise.all([
      this.getAllFromStore(STORES.SYNC_QUEUE),
      this.getAllFromStore(STORES.VITALS_QUEUE),
      this.getAllFromStore(STORES.ADL_QUEUE),
      this.getAllFromStore(STORES.NOTES_QUEUE)
    ]);

    return {
      total: syncQueue.length + vitals.length + adl.length + notes.length,
      clockActions: syncQueue.length,
      vitals: vitals.length,
      adl: adl.length,
      notes: notes.length
    };
  }

  // Sync all queues with server
  async syncAllQueues() {
    if (!navigator.onLine) {
      return { success: false, message: 'Still offline' };
    }

    const results = {
      syncQueue: [],
      vitals: [],
      adl: [],
      notes: [],
      errors: [],
      conflicts: []
    };

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('authToken');

    // Sync clock actions with conflict detection
    const clockActions = await this.getAllFromStore(STORES.SYNC_QUEUE);
    for (const action of clockActions) {
      try {
        // First check server state to detect conflicts
        const shiftResponse = await fetch(`${API_URL}/api/shifts/${action.data.shiftId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const serverShift = shiftResponse.ok ? await shiftResponse.json() : null;

        // Detect conflicts
        const conflicts = syncConflictResolver.detectConflicts(action, serverShift, action.type);
        
        if (conflicts.length > 0) {
          // Add conflicts to resolver for user review
          conflicts.forEach(conflict => syncConflictResolver.addConflict(conflict));
          results.conflicts.push(...conflicts);
          // Don't delete from queue - wait for resolution
          continue;
        }

        // No conflicts - proceed with sync
        const endpoint = action.type === 'CLOCK_IN' 
          ? `/shifts/${action.data.shiftId}/clock-in`
          : `/shifts/${action.data.shiftId}/clock-out`;
        
        const response = await fetch(`${API_URL}/api${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...action.data,
            offline_sync: true,
            offline_timestamp: action.timestamp
          })
        });

        if (response.ok) {
          await this.deleteFromStore(STORES.SYNC_QUEUE, action.id);
          results.syncQueue.push({ id: action.id, success: true });
        } else {
          const errorData = await response.json().catch(() => ({}));
          results.errors.push({ type: 'clock', id: action.id, error: errorData.detail || 'Sync failed' });
        }
      } catch (error) {
        results.errors.push({ type: 'clock', id: action.id, error: error.message });
      }
    }

    // Sync vitals with conflict detection
    const vitals = await this.getAllFromStore(STORES.VITALS_QUEUE);
    for (const vital of vitals) {
      try {
        // Check for recent vitals on server
        const serverResponse = await fetch(`${API_URL}/api/emr/${vital.clientId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const serverData = serverResponse.ok ? await serverResponse.json() : null;

        // Detect conflicts
        const conflicts = syncConflictResolver.detectConflicts(
          vital, 
          { latestVitals: serverData?.vitals?.[0] },
          'VITALS'
        );

        if (conflicts.length > 0) {
          conflicts.forEach(conflict => syncConflictResolver.addConflict(conflict));
          results.conflicts.push(...conflicts);
          continue;
        }

        const response = await fetch(`${API_URL}/api/emr/client/${vital.clientId}/vitals`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...vital.data,
            offline_sync: true,
            offline_timestamp: vital.timestamp
          })
        });

        if (response.ok) {
          await this.deleteFromStore(STORES.VITALS_QUEUE, vital.id);
          results.vitals.push({ id: vital.id, success: true });
        } else {
          results.errors.push({ type: 'vitals', id: vital.id, error: 'Sync failed' });
        }
      } catch (error) {
        results.errors.push({ type: 'vitals', id: vital.id, error: error.message });
      }
    }

    // Sync ADL completions
    const adlItems = await this.getAllFromStore(STORES.ADL_QUEUE);
    for (const adl of adlItems) {
      try {
        const response = await fetch(`${API_URL}/api/shifts/${adl.shiftId}/adl`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...adl.data,
            offline_sync: true,
            offline_timestamp: adl.timestamp
          })
        });

        if (response.ok) {
          await this.deleteFromStore(STORES.ADL_QUEUE, adl.id);
          results.adl.push({ id: adl.id, success: true });
        } else {
          results.errors.push({ type: 'adl', id: adl.id, error: 'Sync failed' });
        }
      } catch (error) {
        results.errors.push({ type: 'adl', id: adl.id, error: error.message });
      }
    }

    // Sync progress notes
    const notes = await this.getAllFromStore(STORES.NOTES_QUEUE);
    for (const note of notes) {
      try {
        const response = await fetch(`${API_URL}/api/emr/client/${note.clientId}/progress-notes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...note.data,
            offline_sync: true,
            offline_timestamp: note.timestamp
          })
        });

        if (response.ok) {
          await this.deleteFromStore(STORES.NOTES_QUEUE, note.id);
          results.notes.push({ id: note.id, success: true });
        } else {
          results.errors.push({ type: 'notes', id: note.id, error: 'Sync failed' });
        }
      } catch (error) {
        results.errors.push({ type: 'notes', id: note.id, error: error.message });
      }
    }

    // Notify if there were conflicts
    if (results.conflicts.length > 0) {
      this.notifyListeners({ 
        isOnline: true, 
        hasConflicts: true, 
        conflictCount: results.conflicts.length 
      });
    }

    return results;
  }

  // Save conflict resolution to history
  async saveConflictResolution(conflict) {
    return this.addToStore(STORES.CONFLICT_HISTORY, conflict);
  }

  // Get conflict history
  async getConflictHistory() {
    return this.getAllFromStore(STORES.CONFLICT_HISTORY);
  }
}

// Create singleton instance
const offlineStorage = new OfflineStorageManager();

export default offlineStorage;
export { STORES };
